<script setup>
import { computed, onMounted, ref } from 'vue'

const props = defineProps({
    text: {
        type: [String, Boolean],
        default: false,
    },
    size: {
        type: [String, Number],
        default: '100%',
    },
    type: {
        type: [String, Boolean],
        default: false, // 'artist', 'show', 'channel', 'genre'
    },
    randomized: {
        type: Boolean,
        default: true,
    },
    tag: {
        type: String,
        default: 'span',
    },
})

const root = ref(null)
const color = ref('black')

function randomizeSize(inputSize, variance = 0.4) {
    return inputSize * (Math.random() * variance + (1 - variance / 2))
}

const styleWidth = computed(() => {
    switch (props.type) {
    case 'artist':
        return `${randomizeSize(9)}em`
    case 'show':
        return `${randomizeSize(10)}em`
    case 'channel':
        return '6em'
    case 'genre':
        return `${randomizeSize(10)}em`
    default:
        break
    }
    return Number.isInteger(props.size)
        ? `${props.randomized ? randomizeSize(props.size) : props.size}em`
        : props.size
})

const shouldDisplay = computed(() => !props.text || props.text.length === 0)

onMounted(() => {
    color.value = window.getComputedStyle(root.value).getPropertyValue('color')
})
</script>

<template>
    <component
        :is="tag"
        ref="root"
    >
        {{ !shouldDisplay ? text : '' }}
        <span
            v-if="shouldDisplay"
            class="placeholder"
            :style="{ width: styleWidth, backgroundColor: shouldDisplay ? color : 'none' }"
        >
            <span class="placeholder-animation" />
        </span>
    </component>
</template>

<style lang="scss" scoped>
@import '@design';

@keyframes placeHolderShimmer {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}

.placeholder {
    position: relative;
    display: inline-block;
    height: 1em;
    overflow: hidden;
    background: $c-shuttle-gray;
    opacity: 0.3;

    &-animation {
        background: linear-gradient(
            to right,
            transparentize($c-black, 1) 8%,
            $c-shuttle-gray 18%,
            transparentize($c-black, 1) 33%
        );
        background-size: fill;
        animation-name: placeHolderShimmer;
        animation-duration: 2s;
        animation-timing-function: linear;
        animation-delay: -1s;
        animation-iteration-count: infinite;
        animation-fill-mode: forwards;

        @include fill;
    }
}
</style>
